// Workshop Scenarios Data

import type {
  WorkshopScenario,
  SimulationStep,
  Template,
} from '../types/workshops';

// ===== DEGAP SIMULATION SCENARIO =====

export const degapSimulationScenario: WorkshopScenario = {
  id: 'degap-company-transformation',
  title: 'DEGAP in Action: Company Transformation',
  type: 'simulation',
  frameworkId: 'pf.degap.stages',
  difficulty: 'intermediate',
  context: 'realigning',
  estimatedTime: 25,
  description: 'Walk through a real-world company transformation scenario, making decisions at each DEGAP stage and seeing their consequences.',
  learningObjectives: [
    'Apply DEGAP stages to a realistic business transformation',
    'Understand the importance of sequence in transformation work',
    'Practice stakeholder engagement and prioritization',
    'See how decisions at each stage impact later stages',
  ],
  prerequisites: ['pf.degap.stages'],
};

export const degapSimulationSteps: SimulationStep[] = [
  // DECIDE STAGE
  {
    id: 'decide-1',
    stage: 'Decide',
    title: 'Recognizing the Need for Change',
    description: 'TechCorp, a mid-size software company, has been losing market share to more agile competitors. Revenue is flat, employee morale is declining, and customer complaints are rising.',
    prompt: 'As the new Head of Strategy, what\'s your first move to address this situation?',
    options: [
      {
        id: 'decide-jump-to-solutions',
        text: 'Immediately launch an initiative to modernize the product suite',
        consequence: 'Your team starts building solutions without understanding root causes or getting buy-in.',
      },
      {
        id: 'decide-frame-the-gap',
        text: 'Define the gap: Where we are (declining competitiveness) vs. where we need to be (market leader in agility)',
        consequence: 'You establish a clear transformation scope and secure executive commitment.',
      },
      {
        id: 'decide-hire-consultants',
        text: 'Hire external consultants to tell us what to do',
        consequence: 'Consultants provide generic recommendations that don\'t stick because there\'s no internal ownership.',
      },
      {
        id: 'decide-wait-and-see',
        text: 'Commission a 6-month study to analyze all options before committing',
        consequence: 'Analysis paralysis sets in while competitors continue to gain ground.',
      },
    ],
    correctOption: 'decide-frame-the-gap',
    feedbackMap: {
      'decide-jump-to-solutions': {
        rating: 'poor',
        message: 'Skipping the Decide stage leads to misaligned efforts',
        explanation: 'Without clearly deciding on the scope of transformation and securing commitment, your initiative will lack direction and support. The DEGAP framework emphasizes that Decide is the foundation - you must frame the gap and commit resources before moving forward.',
        tips: [
          'Always start by clearly defining the gap between current and desired state',
          'Secure executive sponsorship and resource commitment upfront',
          'Establish governance before launching initiatives',
        ],
      },
      'decide-frame-the-gap': {
        rating: 'excellent',
        message: 'Perfect! You\'ve completed the Decide stage correctly',
        explanation: 'By framing the gap and securing commitment, you\'ve established the foundation for a successful transformation. This is exactly what the DEGAP Decide stage calls for: clarity on scope, commitment to act, and governance established.',
        tips: [
          'Document the decision and share it widely',
          'Identify key stakeholders who need to be engaged next',
          'Set clear success criteria for the transformation',
        ],
      },
      'decide-hire-consultants': {
        rating: 'okay',
        message: 'External help can be useful, but ownership is critical',
        explanation: 'While consultants can provide valuable expertise, transformation requires internal ownership. The Decide stage is about your leadership committing to address the gap, not delegating that responsibility.',
        tips: [
          'Use consultants as facilitators or subject matter experts, not decision-makers',
          'Ensure internal teams own the transformation',
          'Build internal capability rather than dependency',
        ],
      },
      'decide-wait-and-see': {
        rating: 'poor',
        message: 'Analysis paralysis prevents action',
        explanation: 'The Decide stage is about committing to act, not endless analysis. While understanding is important, you already have enough information to know change is needed. The DEGAP framework emphasizes decisiveness.',
        tips: [
          'Set a clear deadline for decision-making',
          'Use "good enough" information to decide, then gather more data in later stages',
          'Remember: perfection is the enemy of progress',
        ],
      },
    },
  },

  // ENGAGE STAGE
  {
    id: 'engage-1',
    stage: 'Engage',
    title: 'Building Stakeholder Buy-in',
    description: 'You\'ve defined the transformation scope and secured executive backing. Now you need to engage the broader organization.',
    prompt: 'How do you approach engaging stakeholders across the company?',
    options: [
      {
        id: 'engage-email-announcement',
        text: 'Send an email announcement explaining the transformation plan',
        consequence: 'Most people ignore the email. Those who read it have questions you didn\'t answer.',
      },
      {
        id: 'engage-workshops',
        text: 'Run interactive workshops with key teams to explain the "why" and invite their input',
        consequence: 'Teams feel heard and begin to understand the transformation vision. Engagement builds.',
      },
      {
        id: 'engage-top-down',
        text: 'Have executives cascade the message through the management chain',
        consequence: 'Message gets diluted and distorted as it passes through layers. Frontline staff feel left out.',
      },
      {
        id: 'engage-skip-to-action',
        text: 'Skip engagement and move straight to gathering data - people will get on board when they see results',
        consequence: 'Resistance builds. People don\'t understand the "why" and actively undermine efforts.',
      },
    ],
    correctOption: 'engage-workshops',
    feedbackMap: {
      'engage-email-announcement': {
        rating: 'poor',
        message: 'One-way communication doesn\'t create engagement',
        explanation: 'Engagement requires dialogue, not monologue. The DEGAP Engage stage emphasizes creating shared understanding and enrolling stakeholders, which requires two-way conversation.',
        tips: [
          'Create forums for questions and discussion',
          'Use multiple communication channels',
          'Make it interactive, not just informational',
        ],
      },
      'engage-workshops': {
        rating: 'excellent',
        message: 'Excellent! This is true stakeholder engagement',
        explanation: 'Interactive workshops allow you to explain the "why," address concerns, and gather input. This creates ownership and shared understanding - exactly what the Engage stage is designed to achieve.',
        tips: [
          'Tailor workshops to different stakeholder groups',
          'Document and act on feedback received',
          'Build a coalition of champions from these sessions',
        ],
      },
      'engage-top-down': {
        rating: 'okay',
        message: 'Management cascade has its place, but isn\'t enough',
        explanation: 'While leadership alignment is important, relying solely on cascade communication leads to distortion and excludes voices. The Engage stage requires direct connection with stakeholders.',
        tips: [
          'Supplement cascade with direct engagement',
          'Train managers on how to facilitate engagement',
          'Create feedback loops to correct message distortion',
        ],
      },
      'engage-skip-to-action': {
        rating: 'poor',
        message: 'Skipping Engage leads to resistance and failure',
        explanation: 'People resist what they don\'t understand. The DEGAP framework emphasizes that Engage must come before Gather - you need stakeholder buy-in before asking them to contribute data and insights.',
        tips: [
          'Never skip the Engage stage',
          'Remember: going slow now means going fast later',
          'Engagement is an investment, not overhead',
        ],
      },
    },
  },

  // GATHER STAGE
  {
    id: 'gather-1',
    stage: 'Gather',
    title: 'Collecting Transformation Insights',
    description: 'Stakeholders are engaged and ready to contribute. Now you need to gather data and insights to inform the transformation.',
    prompt: 'What\'s your approach to gathering information?',
    options: [
      {
        id: 'gather-surveys-only',
        text: 'Send out surveys to collect data quickly and efficiently',
        consequence: 'Low response rate, shallow insights, and no richness of context.',
      },
      {
        id: 'gather-multi-method',
        text: 'Use multiple methods: interviews, workshops, data analysis, and observations to build a complete picture',
        consequence: 'You gather rich, contextual insights from multiple perspectives.',
      },
      {
        id: 'gather-external-data',
        text: 'Focus on external market research and competitor analysis',
        consequence: 'You understand the market but miss critical internal dynamics and constraints.',
      },
      {
        id: 'gather-leadership-only',
        text: 'Interview only senior leaders to save time',
        consequence: 'You get the executive view but miss frontline realities and valuable insights.',
      },
    ],
    correctOption: 'gather-multi-method',
    feedbackMap: {
      'gather-surveys-only': {
        rating: 'okay',
        message: 'Surveys alone won\'t give you the full picture',
        explanation: 'While surveys can be efficient, they miss the nuance and context needed for transformation planning. The Gather stage emphasizes collecting rich, multi-faceted information.',
        tips: [
          'Combine quantitative (surveys) with qualitative (interviews) methods',
          'Ask open-ended questions to uncover unexpected insights',
          'Follow up on survey results with deeper investigation',
        ],
      },
      'gather-multi-method': {
        rating: 'excellent',
        message: 'Perfect! You\'re gathering comprehensive insights',
        explanation: 'Using multiple methods ensures you capture both quantitative data and qualitative context, different perspectives, and hidden dynamics. This rich information will inform better decisions in Arrange and Prioritize.',
        tips: [
          'Document insights as you go',
          'Look for patterns across different data sources',
          'Include voices from all levels and functions',
        ],
      },
      'gather-external-data': {
        rating: 'okay',
        message: 'External data is important but incomplete',
        explanation: 'While market intelligence is valuable, transformation requires understanding internal capabilities, culture, and constraints. The Gather stage should include both external and internal perspectives.',
        tips: [
          'Balance external market analysis with internal assessment',
          'Understand what makes your organization unique',
          'Identify internal capabilities that can become competitive advantages',
        ],
      },
      'gather-leadership-only': {
        rating: 'poor',
        message: 'Leadership perspective is necessary but not sufficient',
        explanation: 'Frontline employees often have the best understanding of operational realities, customer pain points, and process inefficiencies. The Gather stage requires input from all levels.',
        tips: [
          'Create safe spaces for frontline input',
          'Recognize that proximity to work often means better insight',
          'Cross-check leadership assumptions against frontline reality',
        ],
      },
    },
  },

  // ARRANGE STAGE
  {
    id: 'arrange-1',
    stage: 'Arrange',
    title: 'Making Sense of Gathered Insights',
    description: 'You\'ve gathered extensive data from multiple sources. Now you need to organize and make sense of it all.',
    prompt: 'How do you approach arranging and synthesizing the gathered information?',
    options: [
      {
        id: 'arrange-spreadsheet',
        text: 'Dump everything into a massive spreadsheet and try to analyze it',
        consequence: 'You drown in data. Patterns are hard to see. Your team is overwhelmed.',
      },
      {
        id: 'arrange-frameworks',
        text: 'Use frameworks (like stakeholder matrices, SWOT, capability maps) to structure insights into meaningful categories',
        consequence: 'Information becomes structured and actionable. Patterns emerge clearly.',
      },
      {
        id: 'arrange-consultant-report',
        text: 'Have consultants synthesize everything into a 200-page report',
        consequence: 'No one reads it. Insights are buried. Teams don\'t internalize the learning.',
      },
      {
        id: 'arrange-gut-feeling',
        text: 'Trust your gut and pick the themes that feel most important',
        consequence: 'You miss important patterns and let confirmation bias drive decisions.',
      },
    ],
    correctOption: 'arrange-frameworks',
    feedbackMap: {
      'arrange-spreadsheet': {
        rating: 'poor',
        message: 'Raw data isn\'t insight',
        explanation: 'The Arrange stage is about structuring information into meaningful patterns, not just organizing it. Frameworks and models help you see relationships and themes that raw data obscures.',
        tips: [
          'Use frameworks to create structure',
          'Look for clusters and themes',
          'Visual representations (maps, matrices) reveal patterns',
        ],
      },
      'arrange-frameworks': {
        rating: 'excellent',
        message: 'Perfect! You\'re creating structured insight',
        explanation: 'Using frameworks to arrange information is exactly what this stage calls for. You\'re turning raw data into structured insight that will inform prioritization. Well done!',
        tips: [
          'Share arrangements with stakeholders for validation',
          'Use multiple frameworks to see different dimensions',
          'Keep it visual and accessible',
        ],
      },
      'arrange-consultant-report': {
        rating: 'poor',
        message: 'Dense reports don\'t create shared understanding',
        explanation: 'The Arrange stage should create insights that teams can internalize and use. Long reports are impressive but not actionable. The DEGAP framework emphasizes usable structure.',
        tips: [
          'Keep syntheses visual and concise',
          'Involve teams in the arranging process',
          'Create artifacts that people will actually use',
        ],
      },
      'arrange-gut-feeling': {
        rating: 'poor',
        message: 'Intuition needs to be validated by structure',
        explanation: 'While experience and intuition are valuable, the Arrange stage provides discipline to validate assumptions and ensure you\'re not missing important patterns. Systematic arrangement prevents bias.',
        tips: [
          'Use frameworks to validate (or challenge) your intuition',
          'Let data surprise you',
          'Involve diverse perspectives in synthesis',
        ],
      },
    },
  },

  // PRIORITIZE STAGE
  {
    id: 'prioritize-1',
    stage: 'Prioritize',
    title: 'Deciding What Matters Most',
    description: 'You\'ve arranged insights into clear patterns. Now comes the critical work: deciding what to tackle first.',
    prompt: 'How do you approach prioritizing transformation initiatives?',
    options: [
      {
        id: 'prioritize-everything',
        text: 'Everything is important - launch all initiatives simultaneously',
        consequence: 'Resources are spread thin. Nothing gets the focus it needs. Teams burn out.',
      },
      {
        id: 'prioritize-criteria',
        text: 'Establish clear criteria (impact, feasibility, dependencies) and rigorously prioritize',
        consequence: 'You create a sequenced roadmap that maximizes value and manageability.',
      },
      {
        id: 'prioritize-loudest',
        text: 'Prioritize based on who argues most forcefully',
        consequence: 'Politics trump strategy. Important but quiet initiatives get ignored.',
      },
      {
        id: 'prioritize-quick-wins',
        text: 'Focus only on quick wins to show momentum',
        consequence: 'Short-term gains but structural issues remain unaddressed. Transformation stalls.',
      },
    ],
    correctOption: 'prioritize-criteria',
    feedbackMap: {
      'prioritize-everything': {
        rating: 'poor',
        message: 'Everything at once means nothing gets done well',
        explanation: 'The Prioritize stage is about making tough choices with limited resources. The DEGAP framework recognizes that sequence and focus are critical to transformation success.',
        tips: [
          'Accept that you can\'t do everything at once',
          'Sequence initiatives to build on each other',
          'Protect focus time and resources for priority items',
        ],
      },
      'prioritize-criteria': {
        rating: 'excellent',
        message: 'Excellent! You\'ve completed DEGAP successfully',
        explanation: 'Using clear criteria to prioritize is exactly what this stage demands. You\'ve now moved through all five DEGAP stages systematically: Decide → Engage → Gather → Arrange → Prioritize. Well done!',
        tips: [
          'Communicate priorities clearly to all stakeholders',
          'Revisit priorities regularly as context changes',
          'Protect priority initiatives from scope creep',
        ],
      },
      'prioritize-loudest': {
        rating: 'poor',
        message: 'Politics shouldn\'t drive prioritization',
        explanation: 'The Prioritize stage should use objective criteria aligned with strategic goals, not organizational politics. Otherwise, you optimize for influence rather than impact.',
        tips: [
          'Establish objective prioritization criteria upfront',
          'Use frameworks like impact/feasibility matrices',
          'Get stakeholder agreement on criteria before prioritizing',
        ],
      },
      'prioritize-quick-wins': {
        rating: 'okay',
        message: 'Quick wins have value, but don\'t ignore structural issues',
        explanation: 'While momentum is important, the Prioritize stage should balance quick wins with addressing root causes. The DEGAP framework emphasizes sustainable transformation.',
        tips: [
          'Include both quick wins and foundational work in your portfolio',
          'Use quick wins to build credibility for harder changes',
          'Don\'t let short-term thinking prevent long-term investment',
        ],
      },
    },
  },
];

// ===== TEMPLATE: DEGAP WORKSHOP DESIGN =====

export const degapWorkshopTemplate: Template = {
  id: 'degap-workshop-design',
  frameworkId: 'pf.degap.stages',
  title: 'DEGAP Workshop Design Template',
  description: 'Plan a workshop or initiative using the five stages of DEGAP as your structure.',
  sections: [
    {
      id: 'decide-section',
      title: '1. Decide: Frame the Transformation',
      description: 'Define the gap and secure commitment',
      fields: [
        {
          id: 'current-state',
          label: 'Current State (As-Is)',
          type: 'textarea',
          placeholder: 'Where are we today? What are the symptoms/problems?',
          required: true,
          helpText: 'Be specific and honest about current reality',
        },
        {
          id: 'desired-state',
          label: 'Desired State (To-Be)',
          type: 'textarea',
          placeholder: 'Where do we need to be? What does success look like?',
          required: true,
          helpText: 'Paint a clear picture of the target state',
        },
        {
          id: 'scope',
          label: 'Transformation Scope',
          type: 'select',
          options: ['Individual', 'Team', 'Organization', 'Ecosystem'],
          required: true,
          helpText: 'What level is this transformation addressing?',
        },
        {
          id: 'commitment',
          label: 'Executive Commitment',
          type: 'textarea',
          placeholder: 'Who is sponsoring this? What resources are committed?',
          required: true,
        },
      ],
    },
    {
      id: 'engage-section',
      title: '2. Engage: Build Stakeholder Buy-in',
      description: 'Enroll stakeholders and create shared understanding',
      fields: [
        {
          id: 'stakeholders',
          label: 'Key Stakeholders',
          type: 'textarea',
          placeholder: 'Who needs to be engaged? List roles/groups.',
          required: true,
        },
        {
          id: 'engagement-approach',
          label: 'Engagement Approach',
          type: 'textarea',
          placeholder: 'How will you engage them? (workshops, 1-on-1s, etc.)',
          required: true,
          helpText: 'Be specific about methods and timing',
        },
        {
          id: 'why-message',
          label: 'The "Why" Message',
          type: 'textarea',
          placeholder: 'What is the compelling reason for this transformation?',
          required: true,
          helpText: 'Craft a clear, compelling narrative',
        },
      ],
    },
    {
      id: 'gather-section',
      title: '3. Gather: Collect Insights',
      description: 'Systematically collect data and perspectives',
      fields: [
        {
          id: 'data-sources',
          label: 'Data Sources',
          type: 'textarea',
          placeholder: 'What information do you need? Where will it come from?',
          required: true,
        },
        {
          id: 'methods',
          label: 'Gathering Methods',
          type: 'multi-select',
          options: ['Interviews', 'Surveys', 'Workshops', 'Data Analysis', 'Observations', 'Document Review'],
          required: true,
          helpText: 'Select all methods you\'ll use',
        },
        {
          id: 'questions',
          label: 'Key Questions to Answer',
          type: 'textarea',
          placeholder: 'What specific questions do you need answered?',
          required: true,
        },
      ],
    },
    {
      id: 'arrange-section',
      title: '4. Arrange: Structure Insights',
      description: 'Organize information into meaningful patterns',
      fields: [
        {
          id: 'frameworks',
          label: 'Frameworks for Arranging',
          type: 'textarea',
          placeholder: 'What frameworks/structures will you use? (e.g., stakeholder matrix, capability map)',
          required: true,
          helpText: 'Choose frameworks that fit your context',
        },
        {
          id: 'synthesis-approach',
          label: 'Synthesis Approach',
          type: 'textarea',
          placeholder: 'How will you synthesize and share insights?',
          required: true,
        },
      ],
    },
    {
      id: 'prioritize-section',
      title: '5. Prioritize: Sequence Actions',
      description: 'Decide what matters most and in what order',
      fields: [
        {
          id: 'criteria',
          label: 'Prioritization Criteria',
          type: 'textarea',
          placeholder: 'What criteria will guide prioritization? (e.g., impact, feasibility, urgency)',
          required: true,
        },
        {
          id: 'top-priorities',
          label: 'Top 3-5 Priorities',
          type: 'textarea',
          placeholder: 'Based on your work, what are the top priorities?',
          required: false,
          helpText: 'This will emerge from the earlier stages',
        },
        {
          id: 'sequencing',
          label: 'Sequencing Rationale',
          type: 'textarea',
          placeholder: 'Why this sequence? What dependencies exist?',
          required: false,
        },
      ],
    },
  ],
};

export const allWorkshopScenarios: WorkshopScenario[] = [
  degapSimulationScenario,
];

export const allTemplates: Template[] = [
  degapWorkshopTemplate,
];

